import { ApolloLink, FetchResult, NextLink, Operation } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { getOperationName } from "@apollo/client/utilities";
import type { GraphQLFormattedError } from "graphql";

interface SentryLinkOptions {
  attachBreadcrumbs?: {
    includeQuery?: boolean;
    includeVariables?: boolean;
    includeError?: boolean;
  };
}

/**
 * Custom Apollo Link for Sentry integration that replaces apollo-link-sentry
 * Compatible with Sentry SDK v9+
 * Written by Seer from Sentry.io
 */
export class CustomSentryLink extends ApolloLink {
  private options: SentryLinkOptions;

  constructor(options: SentryLinkOptions = {}) {
    super();
    this.options = options;
  }

  request(operation: Operation, forward: NextLink) {
    const { attachBreadcrumbs } = this.options;

    // Set transaction name based on operation
    const operationName =
      getOperationName(operation.query) || "Unknown GraphQL Operation";

    // Add breadcrumb for the GraphQL operation
    if (attachBreadcrumbs) {
      const breadcrumb: Sentry.Breadcrumb = {
        category: "graphql",
        type: "query",
        level: "info",
      };

      breadcrumb.data = {};

      if (attachBreadcrumbs.includeQuery) {
        breadcrumb.data.query = operation.query.loc?.source.body;
      }

      if (attachBreadcrumbs.includeVariables && operation.variables) {
        breadcrumb.data.variables = operation.variables;
      }

      breadcrumb.message = `GraphQL ${
        operation.operationName || "anonymous operation"
      }`;

      Sentry.addBreadcrumb(breadcrumb);
    }

    // Continue the operation through the chain
    return forward(operation).map((response: FetchResult) => {
      // Handle errors in the response
      if (response.errors && attachBreadcrumbs?.includeError) {
        response.errors.forEach((error: GraphQLFormattedError) => {
          Sentry.addBreadcrumb({
            category: "graphql",
            type: "error",
            level: "error",
            message: `GraphQL error: ${error.message}`,
            data: {
              operationName,
              error,
            },
          });

          // Capture exception for server errors
          Sentry.captureException(
            new Error(`GraphQL Error: ${error.message}`),
            {
              tags: {
                graphqlOperation: operationName,
              },
              extra: {
                graphqlError: error,
              },
            }
          );
        });
      }

      return response;
    });
  }
}
